import type {
  ConfiguracaoJogo,
  CorTabuleiro,
  PartidaArquivada,
  PartidaPersistida,
  PartidaSerieItem,
  Preferencias,
  SerieArquivada
} from '../types';
import { arquivarPartidaNaConta, historicoDaConta } from './contas';
import { normalizarNivel } from './niveis';

const CORES_TABULEIRO: readonly CorTabuleiro[] = [
  'nogueira',
  'areia',
  'floresta',
  'oceano',
  'ardosia',
  'contraste'
];

function normalizarCorTabuleiro(valor: unknown): CorTabuleiro {
  return CORES_TABULEIRO.includes(valor as CorTabuleiro) ? (valor as CorTabuleiro) : 'nogueira';
}

const CHAVE_PREFERENCIAS = 'xeque-regio:preferencias:v1';
/** Legada (pré-isolamento por jogador). */
const CHAVE_PARTIDA_LEGADA = 'xeque-regio:partida:v1';
const PREFIXO_PARTIDA = 'xeque-regio:partida:v2:';
const CHAVE_ID_NAVEGADOR = 'xeque-regio:navegador-id:v1';
/**
 * Conta lembrada neste navegador (localStorage) — reentra automático na próxima visita.
 * "Sair / trocar conta" limpa e pede senha de novo.
 */
const CHAVE_SESSAO_ATIVA = 'xeque-regio:sessao-ativa:v1';
const CHAVE_ARQUIVO = 'xeque-regio:arquivo-partidas:v1';
const CHAVE_SERIES = 'xeque-regio:series:v1';
const MAX_ARQUIVO = 120;
const MAX_SERIES = 40;

/** Nickname da última conta autenticada neste navegador (entra automático). */
export function obterSessaoAtiva(): string | null {
  try {
    const persistida = localStorage.getItem(CHAVE_SESSAO_ATIVA)?.trim();
    if (persistida && persistida.length >= 2) return persistida;
    // Migra sessão antiga da aba (sessionStorage) para o navegador.
    const legada = sessionStorage.getItem(CHAVE_SESSAO_ATIVA)?.trim();
    if (legada && legada.length >= 2) {
      localStorage.setItem(CHAVE_SESSAO_ATIVA, legada);
      try { sessionStorage.removeItem(CHAVE_SESSAO_ATIVA); } catch { /* ignore */ }
      return legada;
    }
    return null;
  } catch {
    return null;
  }
}

export function definirSessaoAtiva(nome: string): void {
  const n = nome.trim();
  if (n.length < 2) return;
  try {
    localStorage.setItem(CHAVE_SESSAO_ATIVA, n);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(CHAVE_SESSAO_ATIVA);
  } catch {
    /* ignore */
  }
}

export function limparSessaoAtiva(): void {
  try {
    localStorage.removeItem(CHAVE_SESSAO_ATIVA);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(CHAVE_SESSAO_ATIVA);
  } catch {
    /* ignore */
  }
}

/** ID único deste navegador/perfil (não sai do aparelho). */
export function obterIdNavegador(): string {
  try {
    const atual = localStorage.getItem(CHAVE_ID_NAVEGADOR);
    if (atual && atual.length >= 8) return atual;
  } catch {
    /* ignore */
  }
  const id = crypto.randomUUID();
  try {
    localStorage.setItem(CHAVE_ID_NAVEGADOR, id);
  } catch {
    /* ignore */
  }
  return id;
}

function slugSessao(nome: string): string {
  const s = nome
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return s || 'anonimo';
}

function chavePartida(nome: string): string {
  return `${PREFIXO_PARTIDA}${slugSessao(nome)}`;
}

function normalizarPartidaCarregada(partida: PartidaPersistida): PartidaPersistida {
  if (partida.configuracao) {
    partida.configuracao.nivel = normalizarNivel(partida.configuracao.nivel);
    if (partida.configuracao.brancasAuto?.nivel) {
      partida.configuracao.brancasAuto.nivel = normalizarNivel(partida.configuracao.brancasAuto.nivel);
    }
    if (partida.configuracao.pretasAuto?.nivel) {
      partida.configuracao.pretasAuto.nivel = normalizarNivel(partida.configuracao.pretasAuto.nivel);
    }
  }
  return partida;
}

/** Nome humano da config (lado do jogador local). */
function nomeHumanoDaConfig(config: ConfiguracaoJogo): string {
  if (config.modo === 'local') return config.nomeBrancas || config.nomePretas || '';
  const cor = config.corJogador === 'w' ? 'w' : 'b';
  return (cor === 'w' ? config.nomeBrancas : config.nomePretas) || '';
}

export const PREFERENCIAS_PADRAO: Preferencias = {
  tema: 'regio',
  corTabuleiro: 'nogueira',
  somAtivo: true,
  volume: .55,
  reduzirMovimento: false,
  mostrarDestinos: true,
  nivelPreferido: '400',
  controlePreferido: '10-0',
  minutosPersonalizados: 5,
  incrementoPersonalizado: 0,
  nomeJogador: '',
  nomesSalvos: []
};

const CHAVE_CLIENTE_SALA = 'xeque-regio:cliente-sala:v1';

export function carregarPreferencias(): Preferencias {
  try {
    const valor = localStorage.getItem(CHAVE_PREFERENCIAS);
    const base = valor ? { ...PREFERENCIAS_PADRAO, ...JSON.parse(valor) } as Preferencias : { ...PREFERENCIAS_PADRAO };
    base.corTabuleiro = normalizarCorTabuleiro(base.corTabuleiro);
    if (!Array.isArray(base.nomesSalvos)) base.nomesSalvos = [];
    // Garante o nome atual na lista
    if (base.nomeJogador?.trim()) {
      base.nomesSalvos = registrarNomeNaLista(base.nomesSalvos, base.nomeJogador);
    }
    return base;
  } catch {
    return { ...PREFERENCIAS_PADRAO, nomesSalvos: [] };
  }
}
export function salvarPreferencias(preferencias: Preferencias): void {
  const nomes = registrarNomeNaLista(preferencias.nomesSalvos ?? [], preferencias.nomeJogador ?? '');
  localStorage.setItem(CHAVE_PREFERENCIAS, JSON.stringify({ ...preferencias, nomesSalvos: nomes }));
}

function registrarNomeNaLista(lista: string[], nome: string): string[] {
  const n = nome.trim();
  if (n.length < 2) return [...new Set(lista.map((x) => x.trim()).filter(Boolean))].slice(0, 24);
  const limpos = lista.map((x) => x.trim()).filter(Boolean);
  const semDup = limpos.filter((x) => x.toLowerCase() !== n.toLowerCase());
  return [n, ...semDup].slice(0, 24);
}

/** Define o nome ativo e grava na lista de nomes salvos. */
export function definirNomeJogador(preferencias: Preferencias, nome: string): Preferencias {
  const n = nome.trim().slice(0, 40);
  return {
    ...preferencias,
    nomeJogador: n,
    nomesSalvos: registrarNomeNaLista(preferencias.nomesSalvos ?? [], n)
  };
}

export function obterClienteSalaId(): string {
  try {
    const atual = localStorage.getItem(CHAVE_CLIENTE_SALA);
    if (atual && atual.length >= 8) return atual;
  } catch { /* ignore */ }
  const id = crypto.randomUUID();
  try { localStorage.setItem(CHAVE_CLIENTE_SALA, id); } catch { /* ignore */ }
  return id;
}

/**
 * Grava a partida em andamento só para este navegador + este jogador.
 * Outro aparelho / outro perfil / outro nome nunca vê esta chave.
 */
export function salvarPartida(partida: PartidaPersistida, nomeJogador: string): void {
  const dono = nomeJogador.trim();
  if (!dono) return;
  const payload: PartidaPersistida = {
    ...partida,
    donoSessao: dono,
    idNavegador: obterIdNavegador()
  };
  try {
    localStorage.setItem(chavePartida(dono), JSON.stringify(payload));
    localStorage.removeItem(CHAVE_PARTIDA_LEGADA);
  } catch {
    /* quota / modo privado */
  }
}

function tentarMigrarLegada(paraNome: string): PartidaPersistida | null {
  try {
    const raw = localStorage.getItem(CHAVE_PARTIDA_LEGADA);
    if (!raw) return null;
    const partida = JSON.parse(raw) as PartidaPersistida;
    if (partida.versao !== 1) {
      localStorage.removeItem(CHAVE_PARTIDA_LEGADA);
      return null;
    }
    const humano = nomeHumanoDaConfig(partida.configuracao)?.trim();
    const destino = humano || paraNome;
    localStorage.setItem(chavePartida(destino), raw);
    localStorage.removeItem(CHAVE_PARTIDA_LEGADA);
    if (slugSessao(destino) !== slugSessao(paraNome)) return null;
    return normalizarPartidaCarregada(partida);
  } catch {
    return null;
  }
}

/** Carrega a partida deste jogador neste navegador (null se for de outra pessoa). */
export function carregarPartida(nomeJogador?: string | null): PartidaPersistida | null {
  const dono = nomeJogador?.trim();
  if (!dono) return null;
  try {
    const valor = localStorage.getItem(chavePartida(dono));
    if (!valor) {
      const migrada = tentarMigrarLegada(dono);
      if (migrada) return migrada;
      return null;
    }
    const partida = JSON.parse(valor) as PartidaPersistida;
    if (partida.versao !== 1) return null;
    if (partida.donoSessao && slugSessao(partida.donoSessao) !== slugSessao(dono)) return null;
    // Se a partida foi gravada noutro navegador e copiaram o storage (raro), ignora.
    if (partida.idNavegador && partida.idNavegador !== obterIdNavegador()) return null;
    return normalizarPartidaCarregada(partida);
  } catch {
    return null;
  }
}

export function apagarPartida(nomeJogador?: string | null): void {
  const dono = nomeJogador?.trim();
  if (dono) {
    try {
      localStorage.removeItem(chavePartida(dono));
    } catch {
      /* ignore */
    }
  }
  try {
    localStorage.removeItem(CHAVE_PARTIDA_LEGADA);
  } catch {
    /* ignore */
  }
}

/** Partida em andamento deste jogador (ativa). */
export function carregarPartidaAtiva(nomeJogador?: string | null): PartidaPersistida | null {
  const partida = carregarPartida(nomeJogador);
  if (!partida?.ativa) return null;
  return partida;
}

/**
 * Snapshot para rehidratar a tela de partida após HMR / F5 / remount.
 * Preferência: prop `restaurada`; senão localStorage do jogador atual.
 */
export function resolverSnapshotPartida(
  restaurada: PartidaPersistida | null | undefined,
  configuracao?: ConfiguracaoJogo | null,
  nomeJogador?: string | null
): PartidaPersistida | null {
  if (restaurada?.ativa) {
    if (nomeJogador?.trim() && restaurada.donoSessao && slugSessao(restaurada.donoSessao) !== slugSessao(nomeJogador)) {
      return null;
    }
    return restaurada;
  }
  const salva = carregarPartidaAtiva(nomeJogador);
  if (!salva) return null;
  if (!configuracao) return salva;
  // Mesmo modo (duelo, clássico…) — evita misturar partida antiga de outro tipo
  if (salva.configuracao.modo !== configuracao.modo) return null;
  return salva;
}

export function listarPartidasArquivadas(): PartidaArquivada[] {
  try {
    const valor = localStorage.getItem(CHAVE_ARQUIVO);
    if (!valor) return [];
    const lista = JSON.parse(valor) as PartidaArquivada[];
    return Array.isArray(lista) ? lista : [];
  } catch {
    return [];
  }
}

export function arquivarPartida(partida: PartidaArquivada): void {
  const lista = listarPartidasArquivadas().filter((p) => p.id !== partida.id);
  lista.unshift(partida);
  localStorage.setItem(CHAVE_ARQUIVO, JSON.stringify(lista.slice(0, MAX_ARQUIVO)));
  arquivarPartidaNaConta(partida);
}

export function partidasDoJogador(nome: string): PartidaArquivada[] {
  const n = nome.trim();
  if (!n) return [];
  const daConta = historicoDaConta(n);
  if (daConta.length > 0) return daConta;
  const lower = n.toLowerCase();
  return listarPartidasArquivadas().filter(
    (p) =>
      p.nomeJogador.toLowerCase() === lower ||
      p.nomeBrancas.toLowerCase() === lower ||
      p.nomePretas.toLowerCase() === lower
  );
}

function lerSeries(): SerieArquivada[] {
  try {
    const valor = localStorage.getItem(CHAVE_SERIES);
    if (!valor) return [];
    const lista = JSON.parse(valor) as SerieArquivada[];
    return Array.isArray(lista) ? lista : [];
  } catch {
    return [];
  }
}

function gravarSeries(lista: SerieArquivada[]): void {
  localStorage.setItem(CHAVE_SERIES, JSON.stringify(lista.slice(0, MAX_SERIES)));
}

export function listarSeriesArquivadas(): SerieArquivada[] {
  return lerSeries().sort((a, b) => b.atualizadaEm - a.atualizadaEm);
}

export function obterSerieArquivada(id: string): SerieArquivada | null {
  return lerSeries().find((s) => s.id === id) ?? null;
}

export function iniciarSerieArquivada(entrada: {
  id: string;
  modo: SerieArquivada['modo'];
  rotuloA: string;
  rotuloB: string;
  total: number;
  controle?: string;
  inverterACadaPartida?: boolean;
}): SerieArquivada {
  const agora = Date.now();
  const serie: SerieArquivada = {
    id: entrada.id,
    versao: 1,
    iniciadaEm: agora,
    atualizadaEm: agora,
    concluida: false,
    modo: entrada.modo,
    rotuloA: entrada.rotuloA,
    rotuloB: entrada.rotuloB,
    total: entrada.total,
    pontosA: 0,
    pontosB: 0,
    empates: 0,
    partidas: [],
    controle: entrada.controle,
    inverterACadaPartida: entrada.inverterACadaPartida
  };
  const lista = lerSeries().filter((s) => s.id !== serie.id);
  lista.unshift(serie);
  gravarSeries(lista);
  return serie;
}

export function registrarPartidaNaSerie(
  idSerie: string,
  item: PartidaSerieItem,
  placar: { pontosA: number; pontosB: number; empates: number; concluida?: boolean }
): SerieArquivada | null {
  const lista = lerSeries();
  const indice = lista.findIndex((s) => s.id === idSerie);
  if (indice < 0) return null;
  const atual = lista[indice];
  const partidas = [...atual.partidas.filter((p) => p.indice !== item.indice), item].sort((a, b) => a.indice - b.indice);
  const atualizada: SerieArquivada = {
    ...atual,
    atualizadaEm: Date.now(),
    pontosA: placar.pontosA,
    pontosB: placar.pontosB,
    empates: placar.empates,
    partidas,
    concluida: Boolean(placar.concluida) || partidas.length >= atual.total
  };
  lista[indice] = atualizada;
  gravarSeries(lista);
  return atualizada;
}

/** Baixa JSON completo da série (ou de todas). */
export function baixarJson(nomeArquivo: string, dados: unknown): void {
  const blob = new Blob([JSON.stringify(dados, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
}

export function baixarSerieJson(serie: SerieArquivada): void {
  const stamp = new Date(serie.iniciadaEm).toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const slug = `${serie.rotuloA}-vs-${serie.rotuloB}`.replace(/[^\w.-]+/g, '_').slice(0, 80);
  baixarJson(`xeque-regio-serie-${slug}-${stamp}.json`, serie);
}

export function baixarTodasSeriesJson(): void {
  baixarJson(`xeque-regio-series-${new Date().toISOString().slice(0, 10)}.json`, {
    exportadoEm: new Date().toISOString(),
    series: listarSeriesArquivadas(),
    partidasAvulsas: listarPartidasArquivadas()
  });
}

/** Texto PGN multi-game da série. */
export function serieParaPgn(serie: SerieArquivada): string {
  return serie.partidas
    .map((p, i) => {
      const headers = [
        `[Event "Xadrez ${serie.modo}"]`,
        `[Site "local"]`,
        `[Date "${new Date(p.salvaEm).toISOString().slice(0, 10).replace(/-/g, '.')}"]`,
        `[Round "${i + 1}"]`,
        `[White "${p.nomeBrancas}"]`,
        `[Black "${p.nomePretas}"]`,
        `[Result "${p.vencedor === 'w' ? '1-0' : p.vencedor === 'b' ? '0-1' : '1/2-1/2'}"]`,
        `[Series "${serie.rotuloA} vs ${serie.rotuloB}"]`
      ].join('\n');
      return `${headers}\n\n${p.pgn.trim()}\n`;
    })
    .join('\n');
}

export function baixarSeriePgn(serie: SerieArquivada): void {
  const stamp = new Date(serie.iniciadaEm).toISOString().slice(0, 10);
  const blob = new Blob([serieParaPgn(serie)], { type: 'application/x-chess-pgn;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `xeque-regio-serie-${stamp}.pgn`;
  a.click();
  URL.revokeObjectURL(url);
}
